'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { byName } from 'ContentUtils/constants/TemplateTypes';
import Categories from 'ContentUtils/constants/Categories';
import { getPath } from 'DesignData/lib/objectSelectors';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { ECOMMERCE } from 'DesignData/constants/ContentTags';
import { PRIMARY_CSS_PATH } from 'DesignData/constants/PrimaryCss';
import { TEMPLATE_TYPES_WITHOUT_SECTION_DELIMITERS } from 'DesignData/constants/Layouts';
import { SYSTEM, EMAIL, BLOG, PAGE } from 'DesignData/enums/DesignAssetCategories';
import { RESPONSIVE_EMAIL } from 'DesignData/enums/LayoutGeneratorTypes';
import { systemEmailTemplateTypes, systemPageTemplateTypes } from 'DesignData/enums/SystemPageTemplateTypes';
import { hasEmailAccess as getHasEmailAccess } from 'DesignData/selectors/auth';
import { getStylesheetsById, getStylesheetsOrderById } from 'DesignData/selectors/stylesheets';
import { getJavascriptsById, getJavascriptsOrderById } from 'DesignData/selectors/javascripts';
import { getIsVirtualBodyUngated } from 'DesignData/selectors/gates';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';

function getDesignAssetCategory(categoryId) {
  switch (categoryId) {
    case 0:
      return SYSTEM;

    case 2:
      return EMAIL;

    case 3:
      return BLOG;

    case 1:
    case 4:
    case 5:
      return PAGE;

    default:
      return -1;
  }
}
/*
 *
 * Basic Selectors
 *
 */


export const getById = state => state.unifiedLayouts.byId;

const getLayoutIdFromProps = (__, props) => props.layoutId || props.assetId;

const getRevisionIdFromProps = (__, props) => props.revisionId;
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */


export const getLayout = (state, props) => {
  const layoutId = getLayoutIdFromProps(state, props);
  const revisionId = getRevisionIdFromProps(state, props);
  return revisionId ? getPath(state, ['layoutRevisions', 'layouts', layoutId, 'revisions', revisionId, 'layout']) : getPath(state, ['unifiedLayouts', 'byId', layoutId, 'present', 'layout']);
};
/**
 * Required Props:
 *  layoutId
 */

const getIsPublishing = (state, props) => state.unifiedLayouts.isPublishingById[getLayoutIdFromProps(state, props)] || false;

const getIsSaving = (state, props) => state.unifiedLayouts.isSavingById[getLayoutIdFromProps(state, props)] || false;

const getSaveError = (state, props) => state.unifiedLayouts.saveErrorById[getLayoutIdFromProps(state, props)] || false;

const getFetchError = (state, props) => state.unifiedLayouts.fetchErrorById[getLayoutIdFromProps(state, props)] || false;

export const getBodyEditable = (state, props) => !!(getLayout(state, props) || {}).bodyEditable;
export const getIsFetching = (state, props) => state.unifiedLayouts.isFetchingById[getLayoutIdFromProps(state, props)] || false; // TODO: Die. How? Refactor tabs.

const makeGetLayoutsById = () => {
  return createSelector([getById], byId => {
    const newData = {};
    Object.keys(byId).forEach(layoutId => {
      const layout = getPath(byId, [layoutId, 'present', 'layout']);
      if (!layout) return;
      newData[layoutId] = layout;
    });
    return SeamlessImmutable(newData);
  });
};

export const getLayoutsById = makeGetLayoutsById();
/**
 * Required Props:
 *  layoutId || assetId
 */

export const getSnapshot = (state, props) => {
  const layoutId = getLayoutIdFromProps(state, props);
  return SeamlessImmutable.getIn(state, ['unifiedLayouts', 'byId', layoutId], EMPTY_OBJECT);
};
/**
 * Required Props:
 *  layoutId || assetId
 */

const getDataPast = (state, props) => {
  const {
    layoutId
  } = props;
  return getPath(state, ['unifiedLayouts', 'byId', layoutId, 'past']);
};
/**
 * Required Props:
 *  layoutId || assetId
 */


const getDataFuture = (state, props) => {
  const {
    layoutId
  } = props;
  return getPath(state, ['unifiedLayouts', 'byId', layoutId, 'future']);
};

export const getLayoutLabel = (state, props) => {
  const layout = getLayout(state, props);
  return layout && layout.label || null;
};
export const getLayoutTemplateType = (state, props) => {
  const layout = getLayout(state, props);
  return layout && layout.templateType || null;
};
/*
 *
 * Helpers
 *
 */

/**
 * Determines if a `byId` is valid for a layoutId to account for a bug in redux-undo.
 * @see {@link https://github.com/omnidan/redux-undo/pull/111}
 *
 * Just looking for `layout` to be populated.
 * Observation tells us that this will have the default value of `null` if redux-undo created an invalid initial history.
 *
 * @return {Boolean} True if state is valid, false otherwise.
 */

const isValidLayoutDataState = state => !!(state && state.layout);

const getHistoryLength = history => {
  if (!(history && history.length)) return 0;
  return history.filter(isValidLayoutDataState).length;
};
/*
 *
 * Reselelect Selector Factories
 *
 */

/**
 * Get the current state of layout XHR requests
 *
 * @return {Object}
 */


export const makeGetRequestStatus = () => {
  let result = SeamlessImmutable({});
  return createSelector([getIsPublishing, getIsSaving, getSaveError, getFetchError], (isPublishing, isSaving, saveError, fetchError) => {
    return result = result.merge({
      isPublishing,
      isSaving,
      saveError,
      fetchError
    });
  });
};
/**
 * Required Props:
 *  layoutId
 */

export const makeGetPastEditHistoryCount = () => {
  return createSelector(getDataPast, getHistoryLength);
};
/**
 * Required Props:
 *  layoutId
 */

export const makeGetFutureEditHistoryCount = () => {
  return createSelector(getDataFuture, getHistoryLength);
};

function determineVirtualBodyCapable(meta) {
  if (!meta.isPage) {
    return false;
  }

  if (meta.isGlobalGroup) {
    return false;
  }

  return true;
}
/**
 *  @param props
 *  props {
 *    layoutId || assetId
 *  }
 */


export const makeGetIsLayoutReadOnly = () => {
  return createSelector([getHasEmailAccess, getLayout, getIsInLockedFolderFactory], (hasEmailAccess, layout, getIsInLockedFolder) => {
    if (!layout) return true;
    const {
      builtin,
      builtinId,
      isReadOnly,
      categoryId
    } = layout;
    const isEmail = categoryId === Categories.email.id;
    const isLocked = getIsInLockedFolder(layout.folderId);
    return !!(builtin || builtinId || isReadOnly || isEmail && !hasEmailAccess || isLocked);
  });
};
/**
 * Get fundamental properties of a layout.
 */

export const makeGetMeta = () => {
  const getIsReadOnly = makeGetIsLayoutReadOnly();
  let state = SeamlessImmutable({
    layoutId: null,
    folderId: null,
    revisionId: null,
    categoryId: null,
    templateType: null,
    isCss: false,
    isEcommerceEmail: false,
    isEmail: false,
    isPage: false,
    isBlog: false,
    isSystemPage: false,
    isSystemEmail: false,
    isGlobalGroup: false,
    isReadOnly: true,
    isResponsiveEmail: false,
    isKnowledgeArticle: false,
    isVirtualBodyCapable: false,
    isQuote: false,
    hasSectionDelimiters: false,
    designAssetCategory: getDesignAssetCategory(null)
  });
  return createSelector([getLayout, getRevisionIdFromProps, getIsVirtualBodyUngated, getIsReadOnly], (layout, revisionId, isVirtualBodyUngated, isReadOnly) => {
    if (!layout) {
      return state;
    }

    const {
      categoryId,
      contentTags,
      templateType,
      generator
    } = layout;
    const isEmail = categoryId === Categories.email.id;
    const isPage = categoryId === Categories.landing_page.id;
    const isGlobalGroup = templateType === byName.global_group.id;
    const isVirtualBodyCapable = isVirtualBodyUngated && determineVirtualBodyCapable({
      isPage,
      isGlobalGroup
    });
    state = state.merge({
      layoutId: layout.id,
      folderId: layout.folderId,
      revisionId,
      categoryId,
      templateType,
      designAssetCategory: getDesignAssetCategory(categoryId),
      isCss: false,
      isEcommerceEmail: isEmail && contentTags.some(tag => tag.name === ECOMMERCE),
      isEmail,
      isPage,
      isBlog: categoryId === Categories.blog_post.id,
      isSystemEmail: systemEmailTemplateTypes[templateType],
      isSystemPage: systemPageTemplateTypes[templateType],
      isGlobalGroup,
      isReadOnly,
      isResponsiveEmail: isEmail && generator === RESPONSIVE_EMAIL,
      isKnowledgeArticle: categoryId === Categories['knowledge-article'].id,
      isQuote: templateType === byName.quote.id,
      hasSectionDelimiters: !TEMPLATE_TYPES_WITHOUT_SECTION_DELIMITERS.has(templateType),

      /** @type {Boolean} True if layout supports the virtual body feature, false otherwise. */
      isVirtualBodyCapable
    });
    return state;
  });
};
const DEFAULT_ATTACHED_STYLESHEETS = SeamlessImmutable([]);
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const getAttachedStylesheets = (state, props) => {
  const layout = getLayout(state, props);
  return layout && layout.attachedStylesheets || DEFAULT_ATTACHED_STYLESHEETS;
};
const DEFAULT_ATTACHED_JAVASCRIPTS = SeamlessImmutable([]);
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const getAttachedJavascripts = (state, props) => {
  const layout = getLayout(state, props);
  return layout && layout.attachedJs || DEFAULT_ATTACHED_JAVASCRIPTS;
};

function getAvailableTemplates(attached, byId, order) {
  const attachedMap = attached ? attached.reduce((map, {
    id
  }) => {
    map[id] = id;
    return map;
  }, {}) : {};
  const available = order.reduce((collect, id) => {
    if (attachedMap[id]) {
      return collect;
    }

    const template = byId[id];

    if (template.path === PRIMARY_CSS_PATH) {
      return collect;
    }

    collect.push(template);
    return collect;
  }, []);
  return SeamlessImmutable(available);
}
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */


export const makeGetAvailableStylesheets = () => {
  return createSelector([getAttachedStylesheets, getStylesheetsById, getStylesheetsOrderById], getAvailableTemplates);
};
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const makeGetAvailableJavascripts = () => {
  return createSelector([getAttachedJavascripts, getJavascriptsById, getJavascriptsOrderById], getAvailableTemplates);
};
/**
 * Required Props:
 *  layoutId || assetId
 */

export const makeGetMasterLanguage = () => {
  return createSelector([getLayout], layout => layout && layout.masterLanguage);
};
/**
 * Required Props:
 *  layoutId || assetId
 */

export const makeGetLanguages = () => {
  return createSelector([getLayout], layout => {
    if (layout && layout.languages) {
      return SeamlessImmutable([...layout.languages].sort());
    }

    return [];
  });
};